import { useCallback, useEffect, useMemo, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import {
  getWorkCostMap,
  type WorkCostMap,
  type CostMapArea,
  type CostMapChapter,
  type CostMapArticle,
} from "../../../obras/obrasApi";

interface WorkCostMapTabProps {
  workId: number;
  startDate?: string | null;
  endDate?: string | null;
  statusCode?: string | null;
  statusName?: string | null;
  refreshKey?: number;
}

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const formatCurrency = (value: number): string => {
  if (!Number.isFinite(value)) return currencyFormatter.format(0);
  return currencyFormatter.format(value);
};

// Format a deviation percentage (already a percentage value, not a fraction).
const formatPercent = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(1)}%`;
};

// Deviation % for display: only meaningful once real cost exists. A chapter with
// no actual cost yet has a misleading -100% (spent 0 of budget), so render "—".
const formatDesvio = (deviationPercent: number | null, actualCost: number): string =>
  actualCost > 0 ? formatPercent(deviationPercent) : "—";

type OverrunLevel = "none" | "good" | "warning" | "over";

function overrunLevel(
  deviationPercent: number | null,
  hasBudgetedCost: boolean,
  actualCost: number,
  sell: number,
  threshold: number,
): OverrunLevel {
  if (hasBudgetedCost && deviationPercent !== null) {
    if (deviationPercent > threshold) return "over";
    if (deviationPercent > 0) return "warning";
    // Under budget: "good" only when costs were actually incurred — a chapter
    // with no real cost yet (-100%) is "not started", not a saving.
    return actualCost > 0 ? "good" : "none";
  }
  if (!hasBudgetedCost && actualCost > sell && actualCost > 0) return "over";
  return "none";
}

// Text colour for a deviation % cell, by level: red over threshold, amber within
// threshold, green when under budget with real cost, neutral when not yet incurred.
function desvioClass(level: OverrunLevel): string | undefined {
  if (level === "over") return "text-red-600";
  if (level === "warning") return "text-yellow-700";
  if (level === "good") return "text-green-600";
  return undefined;
}

// Always-defined status colour (for chips, bars, accents that need an explicit colour).
function statusColorClass(level: OverrunLevel): string {
  if (level === "over") return "text-red-600";
  if (level === "warning") return "text-yellow-700";
  if (level === "good") return "text-green-600";
  return "text-secondary-text";
}

// Soft tint background matching the status colour (all exist in the static build).
function statusTintClass(level: OverrunLevel): string {
  if (level === "over") return "bg-red-500/10";
  if (level === "warning") return "bg-yellow-500/10";
  if (level === "good") return "bg-green-500/10";
  return "bg-muted";
}

function statusLabel(level: OverrunLevel): string {
  if (level === "over") return "Em derrapagem";
  if (level === "warning") return "Atenção";
  if (level === "good") return "Abaixo do previsto";
  return "Sem custos lançados";
}

// A coloured status pill: tinted background + matching text colour + leading dot.
function StatusChip({ level }: { level: OverrunLevel }) {
  return (
    <span
      className={`${statusTintClass(level)} ${statusColorClass(level)}`}
      style={{
        borderRadius: 999,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: "currentColor" }} />
      {statusLabel(level)}
    </span>
  );
}

function DerrapagemBadge({ level }: { level: OverrunLevel }) {
  if (level === "over") {
    return (
      <span
        className="text-red-600 bg-red-500/10"
        style={{ borderRadius: 6, padding: "1px 8px", fontSize: 12 }}
      >
        Em derrapagem
      </span>
    );
  }
  if (level === "warning") {
    return (
      <span
        className="text-yellow-700 bg-yellow-500/10"
        style={{ borderRadius: 6, padding: "1px 8px", fontSize: 12 }}
      >
        Aviso
      </span>
    );
  }
  return null;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

// Right-aligned numeric cell; negatives are flagged in red.
function MoneyCell({ value, bold }: { value: number; bold?: boolean }) {
  return (
    <td
      className={value < 0 ? "text-red-600" : undefined}
      style={{
        textAlign: "right",
        padding: 8,
        whiteSpace: "nowrap",
        fontWeight: bold ? 700 : undefined,
      }}
    >
      {formatCurrency(value)}
    </td>
  );
}

// Real-margin suffix: " · 23.4%" (empty when the percentage is unknown).
function marginPercentSuffix(marginPercent: number | null): string {
  if (marginPercent === null || !Number.isFinite(marginPercent)) return "";
  return ` · ${marginPercent.toFixed(1)}%`;
}

// Real-margin cell: euro amount + margin %, coloured by sign (green ≥ 0, red < 0).
function MarginRealCell({
  margin,
  marginPercent,
  bold,
}: {
  margin: number;
  marginPercent: number | null;
  bold?: boolean;
}) {
  return (
    <td
      className={margin >= 0 ? "text-green-700" : "text-destructive"}
      style={{
        textAlign: "right",
        padding: 8,
        whiteSpace: "nowrap",
        fontWeight: bold ? 700 : undefined,
      }}
    >
      {formatCurrency(margin)}
      {marginPercentSuffix(marginPercent)}
    </td>
  );
}

interface RowProps {
  expanded: Record<string, boolean>;
  toggle: (key: string) => void;
  threshold: number;
}

// A single article row, indented under its chapter.
function ArticleRow({ article, threshold }: { article: CostMapArticle; threshold: number }) {
  const level = overrunLevel(
    article.deviationPercent,
    article.hasBudgetedCost,
    article.actualCost,
    article.sell,
    threshold,
  );
  return (
    <tr className="border-border" style={{ borderBottomWidth: 1 }}>
      <td style={{ padding: "6px 8px 6px 48px" }} className="text-secondary-text">
        <span style={{ fontSize: 13 }}>
          {article.code ? `${article.code} · ` : ""}
          {article.description || "—"}
        </span>
      </td>
      <MoneyCell value={article.sell} />
      <MoneyCell value={article.budgetedCost} />
      <MoneyCell value={article.actualCost} />
      <MoneyCell value={article.sell - article.budgetedCost} />
      <MarginRealCell margin={article.margin} marginPercent={article.marginPercent} />
      <td
        className={desvioClass(level)}
        style={{ textAlign: "right", padding: 8, whiteSpace: "nowrap" }}
      >
        {formatDesvio(article.deviationPercent, article.actualCost)}
      </td>
    </tr>
  );
}

// A clickable chapter row with a chevron; expands to reveal its articles.
function ChapterRow({
  area,
  chapter,
  expanded,
  toggle,
  threshold,
}: RowProps & { area: CostMapArea; chapter: CostMapChapter }) {
  const key = `${area.name}::${chapter.name}`;
  const isOpen = expanded[key] ?? false;
  const level = overrunLevel(
    chapter.deviationPercent,
    chapter.hasBudgetedCost,
    chapter.actualCost,
    chapter.sell,
    threshold,
  );
  const hasArticles = chapter.articles.length > 0;

  return (
    <>
      <tr className="border-border" style={{ borderBottomWidth: 1 }}>
        <td style={{ padding: "8px 8px 8px 28px" }}>
          <button
            type="button"
            onClick={() => hasArticles && toggle(key)}
            className="text-foreground"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              padding: 0,
              cursor: hasArticles ? "pointer" : "default",
              textAlign: "left",
              fontSize: 14,
            }}
          >
            <span
              className="text-secondary-text"
              style={{
                width: 12,
                display: "inline-block",
                visibility: hasArticles ? "visible" : "hidden",
              }}
            >
              {isOpen ? "▾" : "▸"}
            </span>
            {chapter.name}
          </button>
        </td>
        <MoneyCell value={chapter.sell} />
        <MoneyCell value={chapter.budgetedCost} />
        <MoneyCell value={chapter.actualCost} />
        <MoneyCell value={chapter.sell - chapter.budgetedCost} />
        <MarginRealCell margin={chapter.margin} marginPercent={chapter.marginPercent} />
        <td style={{ textAlign: "right", padding: 8, whiteSpace: "nowrap" }}>
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}
          >
            <DerrapagemBadge level={level} />
            <span className={desvioClass(level)}>
              {formatDesvio(chapter.deviationPercent, chapter.actualCost)}
            </span>
          </span>
        </td>
      </tr>
      {isOpen &&
        chapter.articles.map((article) => (
          <ArticleRow
            key={`${key}::${article.code}::${article.description}`}
            article={article}
            threshold={threshold}
          />
        ))}
    </>
  );
}

// An area header row plus its chapters (areas are expanded by default).
function AreaRows({
  area,
  expanded,
  toggle,
  threshold,
}: RowProps & { area: CostMapArea }) {
  const level = overrunLevel(
    area.deviationPercent,
    area.budgetedCost > 0,
    area.actualCost,
    area.sell,
    threshold,
  );
  return (
    <>
      <tr className="border-border bg-muted" style={{ borderBottomWidth: 1 }}>
        <td style={{ padding: 8, fontWeight: 600 }}>{area.name}</td>
        <MoneyCell value={area.sell} />
        <MoneyCell value={area.budgetedCost} />
        <MoneyCell value={area.actualCost} />
        <MoneyCell value={area.sell - area.budgetedCost} />
        <MarginRealCell margin={area.margin} marginPercent={area.marginPercent} />
        <td
          className={desvioClass(level)}
          style={{ textAlign: "right", padding: 8, whiteSpace: "nowrap", fontWeight: 600 }}
        >
          {formatDesvio(area.deviationPercent, area.actualCost)}
        </td>
      </tr>
      {area.chapters.map((chapter) => (
        <ChapterRow
          key={`${area.name}::${chapter.name}`}
          area={area}
          chapter={chapter}
          expanded={expanded}
          toggle={toggle}
          threshold={threshold}
        />
      ))}
    </>
  );
}

export function WorkCostMapTab({
  workId,
  startDate,
  endDate,
  statusCode,
  statusName,
  refreshKey,
}: WorkCostMapTabProps) {
  const [map, setMap] = useState<WorkCostMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMap(await getWorkCostMap(workId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [workId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // 4.5.5 — schedule overrun, derived client-side from the work's dates.
  const schedule = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const planned = Math.max(0, daysBetween(start, end));
    const elapsed = Math.max(0, daysBetween(start, today));
    const isClosed = statusCode === "encerrada";
    const overdue = !isClosed && today > end ? daysBetween(end, today) : 0;
    return { planned, elapsed, overdue };
  }, [startDate, endDate, statusCode]);

  if (loading) return <div className="text-secondary-text">A carregar mapa de custos…</div>;
  if (error) return <div className="text-red-600">{error}</div>;
  if (!map) return null;

  const threshold = map.overrunThresholdPercent;
  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const overallLevel = overrunLevel(
    map.deviationPercent,
    map.budgetedCost > 0,
    map.actualCost,
    map.sell,
    threshold,
  );

  // Budget consumption (custo real / custo previsto), for the summary meter.
  const consumptionRaw =
    map.budgetedCost > 0 ? (map.actualCost / map.budgetedCost) * 100 : 0;
  const consumptionPct = Math.max(0, Math.min(100, Math.round(consumptionRaw)));

  return (
    <div className="flex flex-col" style={{ gap: 16 }}>
      {/* SUMMARY BAND — two cards */}
      <div className="flex flex-col sm:flex-row" style={{ gap: 12 }}>
        <div
          className="bg-card border border-border"
          style={{ borderRadius: "var(--radius-card)", padding: 16, flex: 1 }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <div className="text-secondary-text" style={{ fontSize: 12 }}>
              Derrapagem de Custo
            </div>
            <StatusChip level={overallLevel} />
          </div>

          {/* Headline — the deviation, just colour (no tinted box). */}
          <div style={{ marginTop: 8 }}>
            <div
              className={desvioClass(overallLevel) ?? "text-foreground"}
              style={{ fontWeight: 700, fontSize: 20, lineHeight: 1.1 }}
            >
              {formatCurrency(map.deviation)}{" "}
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                ({formatDesvio(map.deviationPercent, map.actualCost)})
              </span>
            </div>
            <div className="text-secondary-text" style={{ fontSize: 11, marginTop: 1 }}>
              Desvio do custo · real vs previsto
            </div>
          </div>

          {/* Budget-consumption meter — custo real vs custo previsto. */}
          <div style={{ marginTop: 10 }}>
            <div className="bg-muted" style={{ height: 6, borderRadius: 999, overflow: "hidden" }}>
              <div
                className={statusColorClass(overallLevel)}
                style={{
                  height: "100%",
                  width: `${consumptionPct}%`,
                  backgroundColor: "currentColor",
                  borderRadius: 999,
                }}
              />
            </div>
            <div className="text-secondary-text" style={{ fontSize: 11, marginTop: 4 }}>
              {formatCurrency(map.actualCost)} de {formatCurrency(map.budgetedCost)}
              {map.budgetedCost > 0 ? ` · ${Math.round(consumptionRaw)}% do custo previsto` : ""}
            </div>
          </div>

          {/* Footer — sale value + overrun count, inline. */}
          <div
            className="text-secondary-text"
            style={{ fontSize: 12, marginTop: 10, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}
          >
            <span>
              Venda{" "}
              <span className="text-foreground" style={{ fontWeight: 500 }}>
                {formatCurrency(map.sell)}
              </span>
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Capítulos em derrapagem
              <span
                className={
                  map.overrunChapterCount > 0
                    ? "bg-red-500/10 text-red-600"
                    : "bg-muted text-secondary-text"
                }
                style={{ borderRadius: 999, padding: "0 8px", fontWeight: 600, minWidth: 20, textAlign: "center" }}
              >
                {map.overrunChapterCount}
              </span>
            </span>
          </div>
        </div>
        <div
          className="bg-card border border-border"
          style={{ borderRadius: "var(--radius-card)", padding: 16, flex: 1 }}
        >
          <div className="text-secondary-text" style={{ fontSize: 12 }}>
            Derrapagem de Prazo
          </div>
          {schedule === null ? (
            <div className="text-secondary-text" style={{ marginTop: 8 }}>
              Sem datas de início/fim definidas.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 8 }}>
                <div>
                  <div className="text-secondary-text" style={{ fontSize: 11 }}>Prazo previsto</div>
                  <div className="text-foreground" style={{ fontWeight: 500 }}>
                    {schedule.planned} dias
                  </div>
                </div>
                <div>
                  <div className="text-secondary-text" style={{ fontSize: 11 }}>Decorridos</div>
                  <div className="text-foreground" style={{ fontWeight: 500 }}>
                    {schedule.elapsed} dias
                  </div>
                </div>
                <div>
                  <div className="text-secondary-text" style={{ fontSize: 11 }}>Atraso</div>
                  <div
                    className={schedule.overdue > 0 ? "text-red-600" : "text-foreground"}
                    style={{ fontWeight: 500 }}
                  >
                    {schedule.overdue > 0 ? `${schedule.overdue} dias` : "—"}
                  </div>
                </div>
              </div>
              {statusName && (
                <div style={{ marginTop: 8 }}>
                  <span
                    className="bg-primary/10 text-primary border-primary/20"
                    style={{ borderWidth: 1, borderRadius: 6, padding: "1px 8px", fontSize: 12 }}
                  >
                    {statusName}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* TREE TABLE — Área → Capítulo → Artigo */}
      <div
        className="bg-card border border-border"
        style={{ overflowX: "auto", borderRadius: "var(--radius-card)" }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
          <thead>
            <tr
              className="text-secondary-text border-border bg-muted"
              style={{ borderBottomWidth: 1, textAlign: "right", fontSize: 12 }}
            >
              <th style={{ textAlign: "left", padding: 8 }}>Capítulo / Artigo</th>
              <th style={{ padding: 8 }}>Venda</th>
              <th style={{ padding: 8 }}>Custo previsto</th>
              <th style={{ padding: 8 }}>Custo real</th>
              <th style={{ padding: 8 }}>Margem prevista</th>
              <th style={{ padding: 8 }}>Margem real</th>
              <th style={{ padding: 8 }}>Desvio %</th>
            </tr>
          </thead>
          <tbody>
            {map.areas.map((area) => (
              <AreaRows
                key={area.name}
                area={area}
                expanded={expanded}
                toggle={toggle}
                threshold={threshold}
              />
            ))}
            {map.unmappedActualCost > 0 && (
              <tr className="border-border" style={{ borderBottomWidth: 1 }}>
                <td style={{ padding: 8 }} className="text-secondary-text">
                  — Sem capítulo / Indiretos
                </td>
                <td style={{ textAlign: "right", padding: 8 }}>—</td>
                <td style={{ textAlign: "right", padding: 8 }}>—</td>
                <MoneyCell value={map.unmappedActualCost} />
                <td style={{ textAlign: "right", padding: 8 }}>—</td>
                <td style={{ textAlign: "right", padding: 8 }}>—</td>
                <td style={{ textAlign: "right", padding: 8 }}>—</td>
              </tr>
            )}
            {/* Totais footer */}
            <tr
              className="border-border"
              style={{ borderTopWidth: 2, borderBottomWidth: 0 }}
            >
              <td style={{ padding: 8, fontWeight: 700 }}>Totais</td>
              <td style={{ textAlign: "right", padding: 8, fontWeight: 700, whiteSpace: "nowrap" }}>
                {formatCurrency(map.sell)}
              </td>
              <td style={{ textAlign: "right", padding: 8, fontWeight: 700, whiteSpace: "nowrap" }}>
                {formatCurrency(map.budgetedCost)}
              </td>
              <td style={{ textAlign: "right", padding: 8, fontWeight: 700, whiteSpace: "nowrap" }}>
                {formatCurrency(map.actualCost)}
              </td>
              <MoneyCell value={map.sell - map.budgetedCost} bold />
              <MarginRealCell margin={map.margin} marginPercent={map.marginPercent} bold />
              <td
                className={desvioClass(overallLevel)}
                style={{ textAlign: "right", padding: 8, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {formatDesvio(map.deviationPercent, map.actualCost)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
